import { createFileRoute } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { Quote, Star, ArrowRight } from 'lucide-react'
import { Button } from '../components/ui/button'

export const Route = createFileRoute('/testimonials')({
  component: TestimonialsPage,
})

interface Testimonial {
  name: string
  role: string
  company: string
  image: string
  quote: string
  link?: string
  project: string
  stats: Array<{
    value: string
    label: string
  }>
}

const testimonials: Testimonial[] = [
  {
    name: "M. Anowar Hossain",
    role: "Executive Chairman",
    company: "Growing Together OPC",
    image: "/person/anowar-hossain.webp",
    quote: "LogicNext designed and built a fast, SEO-friendly website for Growing Together OPC. The site improved accessibility, performance, and engagement across devices, helping the organization communicate its mission effectively. Their attention to detail and understanding of our needs exceeded our expectations.",
    link: "https://growingtogether-int.org",
    project: "Website Development",
    stats: [
      { value: "98%", label: "Customer Satisfaction" },
      { value: "3.8x", label: "ROI Improvement" }
    ]
  },
  {
    name: "SM Rakibul Kabir",
    role: "BDS, PGT (Oral Surgery)",
    company: "Dental Practice",
    image: "/person/rakib.png",
    quote: "We built a custom dental management software tailored to clinical workflows — appointments, treatment records, billing, and reporting — streamlining operations and improving day-to-day efficiency across the practice. The system has transformed how we manage patient care.",
    project: "Software Development",
    stats: [
      { value: "4.2x", label: "Team Efficiency" },
      { value: "72%", label: "Reduced Task Time" }
    ]
  },
  {
    name: "Fahim Rahman",
    role: "Founder & CEO",
    company: "TechStart BD",
    image: "/person/placeholder.webp",
    quote: "The mobile app LogicNext developed for us has been a game-changer. Our customer engagement has increased significantly, and the app's performance is outstanding. They delivered on time and within budget, which is rare in this industry.",
    project: "Mobile App Development",
    stats: [
      { value: "50K+", label: "App Downloads" },
      { value: "4.8", label: "App Store Rating" }
    ]
  },
  {
    name: "Nadia Sultana",
    role: "Operations Director",
    company: "DataFlow Solutions",
    image: "/person/placeholder.webp",
    quote: "The AI integration LogicNext implemented has automated 60% of our manual processes. Their team understood our complex requirements and delivered a solution that exceeded our expectations. The chatbot they built handles customer inquiries 24/7 flawlessly.",
    project: "AI & API Integration",
    stats: [
      { value: "60%", label: "Process Automation" },
      { value: "24/7", label: "Customer Support" }
    ]
  }
]

const stats = [
  { value: "4+", label: "Happy Clients" },
  { value: "10+", label: "Projects Delivered" },
  { value: "98%", label: "Satisfaction Rate" },
  { value: "2+", label: "Years Experience" }
]

function TestimonialsPage() {
  return (
    <>
      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-16 container mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <p className="text-sm font-medium text-indigo-400">Testimonials</p>
          <h1 className="text-4xl md:text-6xl font-bold">
            What Our Clients Say
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Real results from real clients. See how we've helped businesses transform
            their digital presence and achieve measurable growth.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative z-10 py-12 container mx-auto px-6">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</div>
              <div className="text-slate-400 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="relative z-10 py-16 container mx-auto px-6">
        <div className="max-w-6xl mx-auto space-y-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="grid md:grid-cols-12 gap-8 items-center bg-slate-900/30 p-8 rounded-3xl border border-white/5 hover:border-white/10 transition-all duration-300"
            >
              {/* Image */}
              <div className="md:col-span-3">
                <div className="aspect-square rounded-2xl overflow-hidden border border-white/10 max-w-50 mx-auto md:max-w-none">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.name)}&background=4f46e5&color=fff&size=200`
                    }}
                  />
                </div>
              </div>

              {/* Content */}
              <div className="md:col-span-6 space-y-4">
                {/* Project Badge */}
                <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full px-3 py-1">
                  <span className="text-indigo-400 text-xs font-medium">{testimonial.project}</span>
                </div>

                {/* Quote */}
                <div className="relative">
                  <Quote className="absolute -top-2 -left-2 h-8 w-8 text-indigo-500/20" />
                  <blockquote className="text-lg leading-relaxed text-slate-300 pl-6">
                    "{testimonial.quote}"
                  </blockquote>
                </div>

                {/* Author */}
                <div className="pt-4">
                  <div className="font-semibold text-white">{testimonial.name}</div>
                  <div className="text-slate-500 text-sm">{testimonial.role}, {testimonial.company}</div>
                  {testimonial.link && (
                    <a
                      href={testimonial.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-400 hover:text-indigo-300 text-sm inline-flex items-center gap-1 mt-1"
                    >
                      {testimonial.link.replace("https://", "")}
                      <ArrowRight className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="md:col-span-3 space-y-6 md:border-l md:border-white/10 md:pl-8">
                {testimonial.stats.map((stat, statIndex) => (
                  <div key={statIndex}>
                    <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-sm text-slate-400">{stat.label}</div>
                  </div>
                ))}
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Privacy Note */}
      <section className="relative z-10 py-8 container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-slate-500 text-sm">
            We value our clients' privacy. Some testimonials use placeholder images and modified details
            to protect client confidentiality while accurately representing the results achieved.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-24 container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center bg-linear-to-b from-indigo-500/10 to-blue-500/10 border border-white/5 rounded-3xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Be Our Next Success Story?</h2>
          <p className="text-slate-400 mb-8">
            Join our growing list of satisfied clients. Let's discuss how we can help
            transform your business with our digital solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-indigo-600 hover:bg-indigo-700">
              <Link to="/contact">Start Your Project</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/10 hover:bg-white/5">
              <Link to="/solutions">View Our Solutions</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}
